// ============================================================================
// ROOM MOVE MODAL (E2 Front Office Workspace)
// ============================================================================
// Mid-stay room move for a CHECKED_IN booking. Target rooms are the currently
// free cells from the Room Board (any category — the E1 allocation concept
// covers cross-category hosting). The vacated room is marked dirty server-side.

import React, { useEffect, useMemo, useState } from 'react';
import IconSystem from 'icon-collection/icon-systems';
import { SyutSelect } from 'components/ui';
import type { SelectOption } from 'components/ui/SyutSelect';
import { frontOfficeService } from 'api/services/frontOfficeService';
import type { RoomBoardRoom } from 'api/types/frontOffice.types';
import notificationService from 'services/notificationService';

interface RoomMoveModalProps {
  isOpen: boolean;
  /** The occupied room cell the move starts from (carries booking context). */
  fromRoom: RoomBoardRoom | null;
  /** Current board snapshot — source of free target rooms. */
  boardRooms: RoomBoardRoom[];
  onClose: () => void;
  onMoved: () => void;
}

const RoomMoveModal: React.FC<RoomMoveModalProps> = ({
  isOpen, fromRoom, boardRooms, onClose, onMoved,
}) => {
  const [toRoomId, setToRoomId] = useState('');
  const [reason, setReason] = useState('');
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (isOpen) {
      setToRoomId('');
      setReason('');
    }
  }, [isOpen]);

  const targetOptions: SelectOption[] = useMemo(
    () => boardRooms
      .filter((r) => !r.isOccupied && !r.isOoo && r.roomId !== fromRoom?.roomId)
      .map((r) => ({
        value: r.roomId,
        label: `${r.roomNumber} · ${r.categoryName || r.categoryCode || ''}`
          + (r.derivedState !== 'AVAILABLE' ? ` (${r.derivedState.toLowerCase()})` : ''),
      })),
    [boardRooms, fromRoom?.roomId],
  );

  if (!isOpen || !fromRoom || !fromRoom.occupantBookingId) return null;

  const handleConfirm = async () => {
    if (!toRoomId) return;
    setSubmitting(true);
    try {
      await frontOfficeService.moveRoom({
        bookingId: fromRoom.occupantBookingId!,
        fromRoomId: fromRoom.roomId,
        toRoomId,
        reason: reason.trim() || undefined,
      });
      notificationService.toast.success(
        `Guest moved from room ${fromRoom.roomNumber}. The vacated room is marked dirty.`,
      );
      onMoved();
      onClose();
    } catch (err: any) {
      const msg = err?.response?.data?.message
        || err?.response?.data?.errors?.[0]
        || err?.message
        || 'Room move failed.';
      notificationService.toast.error(msg);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <>
      <div className="modal-backdrop" onClick={submitting ? undefined : onClose} />
      <div className="modal-container modal-sm">
        <div className="modal-header" style={{ minHeight: 'auto' }}>
          <h2 className="modal-title">Room Move — {fromRoom.roomNumber}</h2>
          <button className="modal-close" onClick={onClose} disabled={submitting}>
            <IconSystem.x size={20} />
          </button>
        </div>

        <div className="modal-content">
          <div style={{
            padding: '0.75rem 0.875rem', background: 'var(--bg-secondary)',
            border: '1px solid var(--border-color)', borderRadius: '6px',
            marginBottom: '0.875rem', fontSize: '0.85rem',
          }}>
            <div><strong>Guest:</strong> {fromRoom.occupantGuestName || '—'}</div>
            <div><strong>Booking:</strong> {fromRoom.occupantBookingNumber}</div>
            <div><strong>Current room:</strong> {fromRoom.roomNumber} · {fromRoom.categoryName || fromRoom.categoryCode}</div>
          </div>

          <div className="form-group">
            <SyutSelect
              label="Move to room"
              required
              options={targetOptions}
              value={toRoomId}
              onChange={(val) => setToRoomId((val as string) || '')}
              placeholder={targetOptions.length ? 'Select a free room…' : 'No free rooms available'}
              size="sm"
            />
          </div>

          <div className="form-group">
            <label className="form-label">Reason</label>
            <input
              type="text"
              className="form-input"
              value={reason}
              maxLength={200}
              onChange={(e) => setReason(e.target.value)}
              placeholder="e.g. AC fault in current room"
              disabled={submitting}
            />
          </div>
        </div>

        <div className="modal-footer" style={{ minHeight: 'auto' }}>
          <button className="btn btn-secondary" onClick={onClose} disabled={submitting}>
            Cancel
          </button>
          <button className="btn btn-primary" onClick={handleConfirm} disabled={submitting || !toRoomId}>
            {submitting ? 'Moving…' : 'Move Room'}
          </button>
        </div>
      </div>
    </>
  );
};

export default RoomMoveModal;
